import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { apiFetch, ApiClientError } from "@/lib/api-client";
import { queryKeys } from "@/lib/query-keys";
import type { DealUpdatePendingRequestInput } from "@colab/shared";

// PATCH /api/portal/deals/:id
//
// Phase 7 sub-bucket 5 — partner edits a submitted-but-pending deal.
//
// Pessimistic. Server gates: must be the partner's row AND status='submitted'
// AND partner-submitted (submitted_by !== null) AND not already withdrawn.
// 409 surfaces as "deal_not_editable" — the form layer renders the toast.
//
// onSuccess invalidates list + detail + timeline (because a field edit may
// recompute the derived `can_edit` flag on the list and the audit row
// changes the timeline).
export function useUpdatePendingDeal(dealId: string) {
  const queryClient = useQueryClient();
  return useMutation<
    { ok: true },
    ApiClientError,
    DealUpdatePendingRequestInput
  >({
    mutationFn: (body) =>
      apiFetch<{ ok: true }>(`/portal/deals/${dealId}`, {
        method: "PATCH",
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.portal.deals.list(),
      });
      void queryClient.invalidateQueries({
        queryKey: queryKeys.portal.deals.detail(dealId),
      });
      void queryClient.invalidateQueries({
        queryKey: queryKeys.portal.deals.timeline(dealId),
      });
    },
    onError: (err) => {
      toast.error(err.message ?? "Failed to update deal");
    },
  });
}
